import { lazyProvider, provideStates } from '@ngxs/store';

import type { TypedRoute } from '@cosmos/router/types';
import { LanguageScope } from '@cosmos/util-translations';
import { customerPortalAuthGuard } from '@customer-portal/auth/data-access-customer-portal-access-guard';
import { PagesToTrackVisit } from '@customer-portal/common/data-access-visit-notification';
import { CustomerPortalDocumentDetailsState } from '@customer-portal/documents/data-access-details';
import { provideInvoicesDataAccess } from '@customer-portal/invoices/data-access-invoices';
import {
  paymentProvidersSettingsResolver,
  paypalPaymentResolver,
} from '@customer-portal/invoices/feature-invoice-payment';

import { resolveDocumentDetails } from '../resolvers';

function getTitle(key: string) {
  return `${LanguageScope.CustomerPortalCommon}.route-titles.${key}`;
}

/**
 * The quote details page, named separately so it can be mounted outside the
 * document-details set as well.
 *
 * A presentation-scoped quote (`/presentations/:presentationId/quotes/:documentId`)
 * is the same page with the same resolver and state — only the parent route
 * differs, which is what makes `resolveDocumentDetails` read it as a
 * project-less document.
 */
export const quoteDetailsRoute: TypedRoute<{
  visitedPage?: PagesToTrackVisit;
}> = {
  path: 'quotes/:documentId',
  resolve: [resolveDocumentDetails],
  canActivate: [
    customerPortalAuthGuard,
    lazyProvider(() => import('@customer-portal/quotes/data-access/store')),
  ],
  loadComponent: async () =>
    (await import('@customer-portal/quotes/feature-quote-details'))
      .CustomerPortalQuoteDetailsPage,
  providers: [provideStates([CustomerPortalDocumentDetailsState])],
  data: {
    visitedPage: PagesToTrackVisit.Quote,
    meta: { title: getTitle('quotes') },
  },
};

export const documentDetailsRoutes: TypedRoute<{
  visitedPage?: PagesToTrackVisit;
}>[] = [
  {
    path: 'orders/:documentId',
    resolve: [resolveDocumentDetails],
    canActivate: [
      customerPortalAuthGuard,
      lazyProvider(() => import('@customer-portal/orders/data-access/store')),
    ],
    loadComponent: async () =>
      (await import('@customer-portal/orders/feature-order-details'))
        .CustomerPortalOrderDetailsPage,
    providers: [provideStates([CustomerPortalDocumentDetailsState])],
    data: {
      visitedPage: PagesToTrackVisit.Order,
      meta: { title: getTitle('orders') },
    },
  },
  quoteDetailsRoute,
  {
    path: 'invoices/:documentId',
    resolve: [
      paymentProvidersSettingsResolver,
      paypalPaymentResolver,
      resolveDocumentDetails,
    ],
    canActivate: [
      customerPortalAuthGuard,
      lazyProvider(() => import('@esp/notes/data-access-notes')),
    ],
    loadComponent: async () =>
      await import('@customer-portal/invoices/feature-invoice-details'),
    providers: [provideInvoicesDataAccess()],
    data: {
      visitedPage: PagesToTrackVisit.Invoice,
      meta: { title: getTitle('invoices') },
    },
  },
] as const;
